import type { Row } from "@tanstack/react-table"

/**
 * A run of consecutive rows that share one platter (`cardRows`): the rows
 * between two group rows, or the whole list when the table isn't grouped.
 *
 * A platter is a stationary bordered box that scrolls its columns inside itself,
 * so every row it contains has to render inside that one box — which is why the
 * rows are grouped into runs before they're laid out.
 */
export interface PlatterSection {
  // Stable across renders: the id of the row that opens the section.
  key: string
  // Inclusive index range into the flat (visible) row model.
  firstIndex: number
  lastIndex: number
}

/**
 * Split the flat row model into platter sections. Group rows are the delimiters
 * and belong to no platter — they render as bands between them.
 */
export function buildPlatterSections<T>(
  rows: Row<T>[],
  isGroupRow: (row: Row<T>) => boolean,
): PlatterSection[] {
  const sections: PlatterSection[] = []
  let open: PlatterSection | undefined
  rows.forEach((row, index) => {
    if (isGroupRow(row)) {
      open = undefined
      return
    }
    if (open) {
      open.lastIndex = index
      return
    }
    open = { key: row.id, firstIndex: index, lastIndex: index }
    sections.push(open)
  })
  return sections
}
